import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../stores/auth-context';

interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { user, logout, isAdmin, isOperator, isAccountant } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const navItems = [
    { to: '/dashboard', label: 'Участники', show: isAdmin || isAccountant },
    { to: '/scan', label: 'Сканирование', show: isOperator },
    { to: '/monitoring', label: 'Мониторинг', show: !isAdmin && !isOperator && !isAccountant },
    { to: '/input-data', label: 'Импорт/экспорт', show: isAdmin },
    { to: '/settings', label: 'Настройки', show: isAdmin || isAccountant },
    { to: '/users', label: 'Управление пользователями', show: isAdmin },
  ];

  function handleLogout() {
    logout();
    navigate('/login');
  }

  const roleLabel: Record<string, string> = {
    admin: 'Администратор',
    operator: 'Оператор',
    monitoring: 'Мониторинг',
    accountant: 'Бухгалтер',
  };

  return (
    <div className="flex min-h-screen bg-ct-bg">
      <nav
        className={`sticky top-0 flex flex-col h-screen transition-all duration-200 ${
          sidebarOpen ? 'w-56' : 'w-0 overflow-hidden'
        }`}
      >
        <div className="m-3 flex flex-1 flex-col rounded-xl bg-ct-surface border border-ct-border shadow-ct-sm overflow-hidden">
          <div className="flex items-center justify-between p-4 shrink-0">
            {sidebarOpen && (
              <div className="text-base font-bold text-ct-text tracking-tight">
                QR Регистрация
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-3 space-y-1">
            {navItems
              .filter((item) => item.show)
              .map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`block rounded-lg px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
                    location.pathname === item.to
                      ? 'bg-gradient-to-r from-ct-primary to-ct-secondary text-ct-on-primary shadow-ct-sm'
                      : 'text-ct-text-muted hover:bg-indigo-50 hover:text-ct-primary'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
          </div>

          <div className="border-t border-ct-border p-4 shrink-0">
            <div className="mb-2 text-xs text-ct-text-muted">
              {user?.username} ({roleLabel[user?.role || ''] || user?.role})
            </div>
            <button
              onClick={handleLogout}
              className="w-full rounded-lg border border-ct-border bg-ct-surface px-3 py-2 text-sm font-semibold text-ct-text-muted transition-all duration-200 hover:bg-slate-50 hover:text-ct-text active:scale-[0.98]"
            >
              Выйти
            </button>
          </div>
        </div>
      </nav>

      <main className="flex-1 min-h-screen overflow-auto p-6">
        <div className="mb-4">
          <button
            onClick={() => setSidebarOpen((p) => !p)}
            className="rounded-lg p-2 text-ct-text-muted transition-all duration-200 hover:bg-indigo-50 hover:text-ct-primary active:scale-[0.98]"
            title={sidebarOpen ? 'Скрыть меню' : 'Показать меню'}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {sidebarOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
        {children}
      </main>
    </div>
  );
}
